import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAppConfig } from '../../context/AppConfigContext';
import {
  Settings,
  Key,
  FolderOpen,
  Save,
  Loader,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Type,
  Sparkles,
  Brain,
  Lock,
} from 'lucide-react';

function ConfigPage() {
  const { appName: currentAppName, setAppName: setGlobalAppName, refreshAppConfig } = useAppConfig();
  const [appName, setAppName] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showProjectId, setShowProjectId] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await adminAPI.getConfig();
      setAppName(res.data.appName || 'Watson');
      setApiKey(res.data.apiKey || '');
      setProjectId(res.data.projectId || '');
      setConfigured(res.data.configured);
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!apiKey) {
      setMessage({ type: 'error', text: 'La API Key es requerida' });
      return;
    }
    if (!projectId) {
      setMessage({ type: 'error', text: 'El Project ID es requerido' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const res = await adminAPI.updateConfig({ apiKey, projectId, appName });
      setMessage({ type: 'success', text: res.data.message });
      setConfigured(true);
      setShowApiKey(false);
      setShowProjectId(false);
      setGlobalAppName(appName);
      refreshAppConfig();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al guardar la configuración'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title={<h1>Configuraci&oacute;n</h1>}>
      {loading ? (
        <div className="admin-loading">
          <Loader size={32} className="loading-spinner" />
          <p>Cargando configuraci&oacute;n...</p>
        </div>
      ) : (
        <div className="config-page-content">
          {/* App Name Section */}
          <div className="config-card config-card-app-name">
            <div className="config-card-header">
              <div className="config-card-icon" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                <Type size={24} />
              </div>
              <div>
                <h2>Nombre de la Aplicaci&oacute;n</h2>
                <p>Este nombre aparecerá en el login, chat y panel de administraci&oacute;n</p>
              </div>
            </div>
            <div className="config-form">
              <div className="form-group">
                <input
                  type="text"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="Nombre que se mostrará en la interfaz"
                />
              </div>
            </div>
          </div>

          {/* Provider Cards Grid */}
          <div className="config-providers-grid">
            {/* IBM watsonx.ai Card */}
            <div className={`config-card config-provider-card ${configured ? 'provider-active' : ''}`}>
              <div className="config-card-header">
                <div className="config-card-icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)' }}>
                  <Settings size={24} />
                </div>
                <div>
                  <h2>IBM watsonx.ai</h2>
                  <p>Modelos IBM Granite</p>
                </div>
              </div>

              <div className="config-status">
                {configured ? (
                  <div className="config-status-badge configured">
                    <CheckCircle size={16} />
                    Configurado
                  </div>
                ) : (
                  <div className="config-status-badge not-configured">
                    <AlertCircle size={16} />
                    No configurado
                  </div>
                )}
              </div>

              <form onSubmit={handleSave} className="config-form">
                <div className="form-group">
                  <label>
                    <Key size={16} />
                    API KEY
                  </label>
                  <div className="input-with-toggle">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Ingresa tu API Key"
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FolderOpen size={16} />
                    PROJECT ID
                  </label>
                  <div className="input-with-toggle">
                    <input
                      type={showProjectId ? 'text' : 'password'}
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      placeholder="Ingresa tu Project ID"
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowProjectId(!showProjectId)}
                    >
                      {showProjectId ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {message && (
                  <div className={`config-message ${message.type}`}>
                    {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                    {message.text}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn-primary config-save-btn"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader size={18} className="loading-spinner" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      Guardar
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* OpenAI Card */}
            <div className="config-card config-provider-card provider-disabled">
              <div className="provider-coming-soon">Comprar</div>
              <div className="config-card-header">
                <div className="config-card-icon" style={{ background: 'linear-gradient(135deg, #10a37f, #0d8c6d)' }}>
                  <Sparkles size={24} />
                </div>
                <div>
                  <h2>OpenAI</h2>
                  <p>Modelos GPT y ChatGPT</p>
                </div>
              </div>

              <div className="config-status">
                <div className="config-status-badge not-configured">
                  <Lock size={16} />
                  No disponible
                </div>
              </div>

              <div className="config-form">
                <div className="form-group">
                  <label>
                    <Key size={16} />
                    API KEY
                  </label>
                  <div className="input-with-toggle">
                    <input
                      type="password"
                      disabled
                      placeholder="sk-..."
                    />
                    <button type="button" className="toggle-visibility" disabled>
                      <Eye size={18} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FolderOpen size={16} />
                    ORGANIZATION ID
                  </label>
                  <input
                    type="text"
                    disabled
                    placeholder="org-..."
                  />
                </div>

                <button className="btn-primary config-save-btn" disabled>
                  <Save size={18} />
                  Guardar
                </button>
              </div>
            </div>

            {/* Google Gemini Card */}
            <div className="config-card config-provider-card provider-disabled">
              <div className="provider-coming-soon">Comprar</div>
              <div className="config-card-header">
                <div className="config-card-icon" style={{ background: 'linear-gradient(135deg, #4285f4, #ea4335)' }}>
                  <Brain size={24} />
                </div>
                <div>
                  <h2>Google Gemini</h2>
                  <p>Modelos Gemini AI</p>
                </div>
              </div>

              <div className="config-status">
                <div className="config-status-badge not-configured">
                  <Lock size={16} />
                  No disponible
                </div>
              </div>

              <div className="config-form">
                <div className="form-group">
                  <label>
                    <Key size={16} />
                    API KEY
                  </label>
                  <div className="input-with-toggle">
                    <input
                      type="password"
                      disabled
                      placeholder="AIza..."
                    />
                    <button type="button" className="toggle-visibility" disabled>
                      <Eye size={18} />
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <FolderOpen size={16} />
                    PROJECT ID
                  </label>
                  <input
                    type="text"
                    disabled
                    placeholder="my-project-id"
                  />
                </div>

                <button className="btn-primary config-save-btn" disabled>
                  <Save size={18} />
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ConfigPage;
